import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Award, Shield, CheckCircle, BadgeCheck, Users, LucideIcon } from 'lucide-react';
import { useJsonData } from '../../hooks/useJsonData';
import { useSafeAnimation, useSafeVariants } from '../ui/SafeMotion';

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  Award, Shield, CheckCircle
};

interface Certification {
  icon: string;
  title: string;
  subtitle: string;
  color: string;
}

interface CertificationsData {
  certifications: Certification[];
}

interface Client {
  name: string;
  logo: string;
}

interface ClientsData {
  clients: Client[];
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export function Clients() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { data: certData, loading: certLoading } = useJsonData<CertificationsData>('/data/certifications.json');
  const { data: clientsData, loading: clientsLoading } = useJsonData<ClientsData>('/data/clients.json');

  // SSR-safe animation props
  const headerAnim = useSafeAnimation(
    { opacity: 0, y: 30 },
    { opacity: 1, y: 0 },
    isInView
  );
  const badgeAnim = useSafeAnimation(
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 },
    isInView
  );
  const bannerAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 },
    isInView
  );
  const variants = useSafeVariants(isInView);

  const certifications = certData?.certifications || [];
  const clients = clientsData?.clients || [];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white overflow-hidden relative">
      {/* Background Decoration */}
      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-eco-green-50 to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={headerAnim.initial}
          animate={headerAnim.animate}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.span
            initial={badgeAnim.initial}
            animate={badgeAnim.animate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-eco-green-50 border border-eco-green-200 text-eco-green-700 text-sm font-semibold rounded-full mb-6"
          >
            <BadgeCheck className="w-4 h-4" />
            Trusted & Certified
          </motion.span>
          <h2 className="text-4xl sm:text-5xl font-bold text-eco-green-900 mb-4">
            Our Clients
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Trusted by leading cement manufacturers, FMCG companies, and sustainability pioneers
          </p>
        </motion.div>

        {/* Client Logos Grid */}
        {!clientsLoading && clients.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial={variants.initial}
            animate={variants.animate}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 mb-16"
          >
            {clients.map((client) => (
              <motion.div
                key={client.name}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-xl p-4 flex items-center justify-center h-24 shadow-md border border-gray-100 hover:shadow-lg hover:border-eco-green-200 transition-all"
              >
                <img
                  src={client.logo}
                  alt={client.name}
                  className="max-h-16 max-w-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Loading State for Clients */}
        {clientsLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 mb-16">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        )}

        {/* Certifications */}
        {!certLoading && certifications.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial={variants.initial}
            animate={variants.animate}
            className="flex flex-wrap justify-center gap-6 mb-12"
          >
            {certifications.map((cert) => {
              const IconComponent = iconMap[cert.icon] || Award;
              return (
                <motion.div
                  key={cert.title}
                  variants={itemVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="bg-white rounded-2xl p-6 flex items-center gap-5 min-w-[280px] shadow-lg shadow-eco-green-100/50 border border-eco-green-100 hover:border-eco-green-300 hover:shadow-xl transition-all"
                >
                  <div className={`bg-gradient-to-br ${cert.color} p-4 rounded-xl shadow-lg`}>
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-eco-green-900 text-lg">{cert.title}</p>
                    <p className="text-sm text-gray-600">{cert.subtitle}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Loading State for Certifications */}
        {certLoading && (
          <div className="flex flex-wrap justify-center gap-6 mb-12">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 w-72 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        )}

        {/* Client Trust Banner */}
        <motion.div
          initial={bannerAnim.initial}
          animate={bannerAnim.animate}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-r from-eco-green-600 to-eco-green-700 rounded-2xl p-8 text-center shadow-xl"
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <Users className="w-8 h-8 text-white" />
            <span className="text-5xl font-bold text-white">30+</span>
          </div>
          <p className="text-eco-green-100 text-lg">
            Trusted clients including retail chains, manufacturing units, FMCG companies, and warehouses
          </p>
        </motion.div>
      </div>
    </section>
  );
}
